import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import MainLayout from './components/MainLayout';
import DashboardPage from './pages/DashboardPage';
import DataScreenPage from './pages/DataScreenPage';
import AccommodationManagementPage from './pages/AccommodationManagementPage';
import DormitoryManagementPage from './pages/DormitoryManagementPage';
import DormitoryInspectionPage from './pages/DormitoryInspectionPage';
import StudentManagementPage from './pages/StudentManagementPage';
import TeacherManagementPage from './pages/TeacherManagementPage';
import ClassManagementPage from './pages/ClassManagementPage';
import AttendanceManagementPage from './pages/AttendanceManagementPage';
import AttendanceSessionPage from './pages/AttendanceSessionPage';
import AttendanceHistoryPage from './pages/AttendanceHistoryPage';
import AttendanceDetailPage from './pages/AttendanceDetailPage';
import LeaveManagementPage from './pages/LeaveManagementPage';
import InterviewManagementPage from './pages/InterviewManagementPage';
import RepairManagementPage from './pages/RepairManagementPage';
import OperationLogPage from './pages/OperationLogPage';
import api from './api';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsAuthenticated(false);
            return;
        }

        const verifySession = async () => {
            try {
                await api.get('/auth/me');
                setIsAuthenticated(true);
            } catch (error) {
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                setIsAuthenticated(false);
            }
        };

        verifySession();
    }, []);

    if (isAuthenticated === null) {
        return null;
    }

    return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Role-based Route Component
const RoleRoute = ({ children, allowedRoles }) => {
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRole = async () => {
            try {
                const response = await api.get('/auth/me');
                setUserRole(response.data.role);
            } catch (error) {
                console.error("Failed to fetch user role");
            }
            setLoading(false);
        };
        fetchRole();
    }, []);

    if (loading) return null;
    
    if (!userRole || !allowedRoles.includes(userRole)) {
        // 根据角色重定向到对应的首页
        if (userRole === 'CLASS_TEACHER' || userRole === 'TEACHING_ADMIN') {
            return <Navigate to="/students" replace />;
        } else if (userRole === 'LOGISTICS_ADMIN' || userRole === 'LOGISTICS_TEACHER') {
            return <Navigate to="/repair" replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Component to handle root redirection based on role
const RootRedirect = () => {
    const [redirectPath, setRedirectPath] = useState(null);

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const response = await api.get('/auth/me');
                const role = response.data.role;
                if (role === 'CLASS_TEACHER') {
                    setRedirectPath('/students');
                } else if (role === 'TEACHING_ADMIN') {
                    setRedirectPath('/dashboard');
                } else if (role === 'LOGISTICS_ADMIN' || role === 'LOGISTICS_TEACHER') {
                    setRedirectPath('/repair');
                } else {
                    setRedirectPath('/dashboard');
                }
            } catch (error) {
                console.error("Failed to fetch user info for redirect", error);
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                setRedirectPath('/login');
            }
        };
        fetchUserRole();
    }, []);

    if (!redirectPath) {
        return null;
    }

    return <Navigate to={redirectPath} replace />;
};

function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            {/* Nested routes within MainLayout */}
            <Route index element={<RootRedirect />} />
            <Route path="dashboard" element={<RoleRoute allowedRoles={['ADMIN', 'TEACHING_ADMIN']}><DashboardPage /></RoleRoute>} />
            <Route path="data-screen" element={<RoleRoute allowedRoles={['LOGISTICS_ADMIN']}><DataScreenPage /></RoleRoute>} />
            <Route path="accommodation" element={<RoleRoute allowedRoles={['LOGISTICS_ADMIN']}><AccommodationManagementPage /></RoleRoute>} />
            <Route path="dormitories" element={<RoleRoute allowedRoles={['LOGISTICS_ADMIN']}><DormitoryManagementPage /></RoleRoute>} />
            <Route path="dormitory-inspection" element={<RoleRoute allowedRoles={['LOGISTICS_ADMIN']}><DormitoryInspectionPage /></RoleRoute>} />
            <Route path="students" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><StudentManagementPage /></RoleRoute>} />
            <Route path="teachers" element={<RoleRoute allowedRoles={['ADMIN']}><TeacherManagementPage /></RoleRoute>} />
            <Route path="classes" element={<RoleRoute allowedRoles={['ADMIN', 'TEACHING_ADMIN']}><ClassManagementPage /></RoleRoute>} />
            <Route path="attendance" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><AttendanceManagementPage /></RoleRoute>} />
            <Route path="attendance/session/:id" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><AttendanceSessionPage /></RoleRoute>} />
            <Route path="attendance/history" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><AttendanceHistoryPage /></RoleRoute>} />
            <Route path="attendance/history/:id" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><AttendanceDetailPage /></RoleRoute>} />
            <Route path="leave" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><LeaveManagementPage /></RoleRoute>} />
            <Route path="interview" element={<RoleRoute allowedRoles={['ADMIN', 'CLASS_TEACHER', 'TEACHING_ADMIN']}><InterviewManagementPage /></RoleRoute>} />
            <Route path="repair" element={<RoleRoute allowedRoles={['ADMIN', 'LOGISTICS_ADMIN', 'LOGISTICS_TEACHER']}><RepairManagementPage /></RoleRoute>} />
            <Route path="logs" element={<RoleRoute allowedRoles={['ADMIN']}><OperationLogPage /></RoleRoute>} />
          </Route>
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
